"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, HelpCircle, Eye, Pencil, ShieldCheck, Check } from "lucide-react";
import { useWorkAreaContext } from "@/contexts/workAreaContext";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import EmojiPicker from "@/components/tailwind/editor/EmojiPicker";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import {
  giveGroupAccessToWorkArea,
  type GroupAccessPermission,
} from "@/services-frontend/workspace/groupServices";
import { GlobeGlyph, PeopleGlyph, LockGlyph, KeypadGlyph, CheckGlyph } from "@/components/tailwind/ui/icons/glyphs";

interface CreateWorkAreaModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When set, the modal creates a private work area and immediately grants
   * this group access at the chosen permission, instead of showing the
   * normal Open/Private picker. Used by the "Create work area" action on a
   * group row so it goes through the same creation path (context state,
   * home-page seed, navigation) as the sidebar "+" button.
   */
  shareWithGroup?: { groupId: string; groupName: string };
  /** Pre-fills the name field when the modal opens. */
  defaultName?: string;
}

type AccessLevel ="open" | "private";

export default function CreateWorkAreaModal({
  isOpen,
  onClose,
  shareWithGroup,
  defaultName,
}: CreateWorkAreaModalProps) {
  const { createWorkspaceWorkArea, refreshWorkAreas } = useWorkAreaContext();
  const { currentWorkspace } = useWorkspaceContext();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [accessLevel, setAccessLevel] = useState<AccessLevel>("open");
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] = useState(false);
  const [groupPermission, setGroupPermission] = useState<GroupAccessPermission>("edit");
  const [isGroupPermissionDropdownOpen, setIsGroupPermissionDropdownOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const accessButtonRef = useRef<HTMLButtonElement>(null);
  const accessDropdownRef = useRef<HTMLDivElement>(null);
  const groupPermissionButtonRef = useRef<HTMLButtonElement>(null);
  const groupPermissionDropdownRef = useRef<HTMLDivElement>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setIcon("");
      setAccessLevel("open");
      setIsAccessDropdownOpen(false);
      setGroupPermission("edit");
      setIsGroupPermissionDropdownOpen(false);
      setShowEmojiPicker(false);
    }
  }, [isOpen]);

  // Pre-fill the name when opening (e.g. "<group name> workarea").
  useEffect(() => {
    if (isOpen && defaultName) {
      setName(defaultName);
    }
  }, [isOpen, defaultName]);


  // Close modal on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Close access dropdown when clicking outside
  useEffect(() => {
    if (!isAccessDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (accessDropdownRef.current && accessDropdownRef.current.contains(target)) return;
      if (accessButtonRef.current && accessButtonRef.current.contains(target)) return;
      setIsAccessDropdownOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsAccessDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [isAccessDropdownOpen]);

  // Close group permission dropdown when clicking outside
  useEffect(() => {
    if (!isGroupPermissionDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (groupPermissionDropdownRef.current && groupPermissionDropdownRef.current.contains(target)) return;
      if (groupPermissionButtonRef.current && groupPermissionButtonRef.current.contains(target)) return;
      setIsGroupPermissionDropdownOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsGroupPermissionDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [isGroupPermissionDropdownOpen]);

  const handleAccessChange = (newAccessLevel: AccessLevel) => {
    setAccessLevel(newAccessLevel);
    setIsAccessDropdownOpen(false);
  };

  const handleGroupPermissionChange = (newPermission: GroupAccessPermission) => {
    setGroupPermission(newPermission);
    setIsGroupPermissionDropdownOpen(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a work area name");
      return;
    }

    setIsCreating(true);
    try {
      // Sharing with a group always creates a private work area — an open
      // one would make the explicit group grant redundant.
      const levelToSend = shareWithGroup ? "private" : accessLevel;
      const workArea = await createWorkspaceWorkArea(
        name.trim(),
        description.trim() || undefined,
        icon.trim() || undefined,
        levelToSend as "open" | "private"
      );

      if (!workArea) {
        // createWorkspaceWorkArea already toasted its own failure reason.
        return;
      }

      if (shareWithGroup) {
        await giveGroupAccessToWorkArea({
          workAreaId: workArea._id,
          groupId: shareWithGroup.groupId,
          permission: groupPermission,
        });
        // createWorkspaceWorkArea already added the bare work area to
        // context state (before it had group access) — refetch so the
        // group shows up without waiting for a reload.
        await refreshWorkAreas(true);
        toast.success(`Created "${name.trim()}" and shared with ${shareWithGroup.groupName}`);
      }

      onClose();
    } catch (error) {
      console.error("Error creating work area:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create work area");
    } finally {
      setIsCreating(false);
    }
  };

  const getAccessDisplay = (level: AccessLevel) => {
    switch (level) {
      case "open":
        return "Open";
      case "private":
        return "Private";
      default:
        return "Open";
    }
  };

  const getAccessDescription = (level: AccessLevel) => {
    switch (level) {
      case "open":
        return "Anyone can see and join this workarea";
      case "private":
        return "Only members can see that this workarea exists";
      default:
        return "Anyone can see and join this workarea";
    }
  };

  const accessDisplay = getAccessDisplay(accessLevel);
  const accessDescription = getAccessDescription(accessLevel);

  const getGroupPermissionDisplay = (permission: GroupAccessPermission) => {
    switch (permission) {
      case "view":
        return "View";
      case "edit":
        return "Edit";
      case "full":
        return "Full";
      default:
        return "Edit";
    }
  };

  const getGroupPermissionDescription = (permission: GroupAccessPermission) => {
    switch (permission) {
      case "view":
        return "Can view pages in this workarea";
      case "edit":
        return "Can view and edit pages in this workarea";
      case "full":
        return "Can view, edit, and manage this workarea";
      default:
        return "Can view and edit pages in this workarea";
    }
  };

  const getGroupPermissionIcon = (permission: GroupAccessPermission) => {
    switch (permission) {
      case "view":
        return <Eye className="w-5 h-5 text-black dark:text-white flex-shrink-0" />;
      case "edit":
        return <Pencil className="w-5 h-5 text-black dark:text-white flex-shrink-0" />;
      case "full":
        return <ShieldCheck className="w-5 h-5 text-black dark:text-white flex-shrink-0" />;
      default:
        return <Pencil className="w-5 h-5 text-black dark:text-white flex-shrink-0" />;
    }
  };

  const groupPermissionDisplay = getGroupPermissionDisplay(groupPermission);
  const groupPermissionDescription = getGroupPermissionDescription(groupPermission);

  if (!isOpen || typeof window === "undefined") return null;

  return createPortal(
    <div
      // Bottom-anchored on mobile (bottom sheet) so it slides up from
      // the bottom and stays in the thumb zone; centered on desktop.
      className="fixed inset-0 z-[11000] flex items-end md:items-center justify-center"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/30 dark:bg-black/60" onClick={onClose} />
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // Mobile: full width, top corners only, max 90dvh with overflow scroll
        //   so the (name + members list + permissions) content can scroll
        //   under the iOS keyboard. Desktop: original 480px width, fully
        //   rounded, with the original viewport calc cap.
        className="relative bg-modal shadow-lg border border-component flex flex-col w-full md:w-[480px] md:max-w-[calc(100vw-100px)] rounded-t-md md:rounded-lg max-h-[90dvh] overflow-y-auto clean-scrollbar"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {/* Content */}
        <div className="p-[26px] flex flex-col items-center">
          {/* Title */}
          <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
            {shareWithGroup ? `Create workarea for ${shareWithGroup.groupName}` : "Create a new Workarea"}
          </div>
          <div className="text-xs leading-4 text-zinc-600 dark:text-zinc-400 mb-[26px] text-center">
            {shareWithGroup
              ? "A private workarea is created and the group is granted access at the chosen permission."
              : "Workareas are where your team organizes pages, permissions, and members"}
          </div>

          {/* Icon & Name */}
          <div className="w-full mb-4">
            <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">
              Icon & name
            </label>
            <div className="flex items-start gap-2">
              {/* Icon Display */}
              <div className="flex-shrink-0">
                <button
                  ref={iconButtonRef}
                  type="button"
                  title="Choose an icon"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="w-[26px] h-[26px] rounded bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center hover-component  transition-colors cursor-pointer"
                >
                  {icon ? (
                    <span className="flex items-center justify-center">{renderIcon(icon, { style: { width: 16, height: 16 } })}</span>
                  ) : (
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 uppercase">
                      {name.charAt(0) || "T"}
                    </span>
                  )}
                </button>
                {showEmojiPicker && (
                  <EmojiPicker
                    anchorRef={iconButtonRef}
                    currentEmoji={icon}
                    onSelect={(emoji) => { setIcon(emoji); setShowEmojiPicker(false); }}
                    onClose={() => setShowEmojiPicker(false)}
                    onRemove={icon ? () => { setIcon(""); setShowEmojiPicker(false); } : undefined}
                  />
                )}
              </div>
              {/* Name Input */}
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="e.g. Acme Inc."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-[10px] py-1 text-sm leading-5 rounded-md border border-component bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  maxLength={50}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="w-full mb-4">
            <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">
              Description
            </label>
            <textarea
              placeholder="Details about your workarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={200}
              className="w-full px-[10px] py-1 text-sm leading-5 rounded-md border border-component bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Permissions */}
          {shareWithGroup ? (
            <div className="w-full mb-6">
              <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                Group permission
              </label>
              <div className="relative">
                <button
                  ref={groupPermissionButtonRef}
                  type="button"
                  title="Choose the group's permission level"
                  onClick={() => setIsGroupPermissionDropdownOpen(!isGroupPermissionDropdownOpen)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1 rounded-md border transition-colors text-sm min-h-[45px]",
                    isGroupPermissionDropdownOpen
                      ? "bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-[rgb(42,42,42)]"
                      : "bg-white dark:bg-zinc-800 border-zinc-200 dark:border-[rgb(42,42,42)] hover:bg-zinc-50 dark:hover:bg-[rgb(42,42,42)]"
                  )}
                >
                  <div className="flex items-center justify-center min-w-[20px] min-h-[20px]">
                    {getGroupPermissionIcon(groupPermission)}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                      {groupPermissionDisplay}
                    </div>
                    <div className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
                      {groupPermissionDescription}
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                </button>
              </div>
            </div>
          ) : (
          <div className="w-full mb-6">
            <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">
              Permissions
            </label>
            <div className="relative">
              <button
                ref={accessButtonRef}
                title="Choose who can access this work area"
                onClick={() => setIsAccessDropdownOpen(!isAccessDropdownOpen)}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1 rounded-md border transition-colors text-sm min-h-[45px]",
                  isAccessDropdownOpen
                    ? "bg-zinc-100 dark:bg-zinc-800 border-component"
                    : "bg-white dark:bg-zinc-800 border-component hover-component "
                )}
              >
                <div className="flex items-center justify-center min-w-[20px] min-h-[20px]">
                  {accessLevel === "open" ? (
                    <GlobeGlyph className="w-4 h-4 fill-current flex-shrink-0" />
                  ) : accessLevel === "private" ? (
                    <PeopleGlyph className="w-4 h-4 fill-current flex-shrink-0" />
                  ) : (
                    <LockGlyph className="w-4 h-4 fill-current flex-shrink-0" />
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                    {accessDisplay}
                  </div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
                    {accessDescription}
                  </div>
                </div>
                <ChevronDown className="w-3 h-3 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
              </button>
            </div>
          </div>
          )}

          {/* Footer */}
          <div className="w-full flex items-center justify-between pt-[22px]">
            <a
              // href="#"
              // target="_blank"
              // rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 px-1.5 py-1 rounded-md hover-component  transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Learn about workareas</span>
            </a>
            <div className="flex gap-2">
              <button
                title="Cancel"
                onClick={onClose}
                disabled={isCreating}
                className="px-3 py-1.5 text-sm rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 hover-component  transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                title="Create work area"
                onClick={handleCreate}
                disabled={!name.trim() || isCreating}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-md font-medium transition-colors",
                  !name.trim() || isCreating
                    ? "opacity-40 cursor-not-allowed bg-blue-500 text-white"
                    : "bg-blue-500 text-white hover:bg-blue-700"
                )}
              >
                {isCreating ? "Creating..." : "Create Work Area"}
              </button>
            </div>
          </div>
        </div>

        {/* Access Level Dropdown */}
        {isAccessDropdownOpen && typeof window !== "undefined" && createPortal(
          <div
            ref={accessDropdownRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fixed z-[12000] bg-modal shadow-lg border border-component rounded-lg p-1 min-w-[280px] flex flex-col gap-[1px]"
            style={{
              top: accessButtonRef.current
                ? accessButtonRef.current.getBoundingClientRect().bottom + 4
                : 0,
              left: accessButtonRef.current
                ? accessButtonRef.current.getBoundingClientRect().left
                : 0,
            }}
          >

            {/* Open Option */}
            <button
              title="Open: anyone in the workspace can access"
              onClick={() => handleAccessChange("open")}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1 rounded-md transition-colors min-h-[45px]",
                accessLevel === "open"
                  ? "bg-zinc-100 dark:bg-[rgb(42,42,42)]"
                  : "hover-component "
              )}
            >
              <div className="flex items-center justify-center min-w-[20px] min-h-[20px]">
                <KeypadGlyph className="w-4 h-4 fill-current flex-shrink-0" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm text-zinc-900 dark:text-zinc-100">Open</div>
                <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">
                  {getAccessDescription("open")}
                </div>
              </div>
              {accessLevel === "open" && (
                <CheckGlyph className="w-4 h-4 fill-current flex-shrink-0" />
              )}
            </button>

            {/* Private Option */}
            <button
              title="Private: only invited members can access"
              onClick={() => handleAccessChange("private")}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1 rounded-md transition-colors min-h-[45px]",
                accessLevel === "private"
                  ? "bg-zinc-100 dark:bg-[rgb(42,42,42)]"
                  : "hover-component "
              )}
            >
              <div className="flex items-center justify-center min-w-[20px] min-h-[20px]">
                <LockGlyph className="w-4 h-4 fill-current flex-shrink-0" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm text-zinc-900 dark:text-zinc-100">Private</span>
                </div>
                <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">
                  {getAccessDescription("private")}
                </div>
              </div>
            </button>
          </div>,
          document.body
        )}

        {/* Group Permission Dropdown */}
        {isGroupPermissionDropdownOpen && typeof window !== "undefined" && createPortal(
          <div
            ref={groupPermissionDropdownRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fixed z-[12000] bg-white dark:bg-[hsl(0deg_0%_12.55%)] shadow-lg border border-zinc-200 dark:border-[rgb(42,42,42)] rounded-lg p-1 min-w-[280px] flex flex-col gap-[1px]"
            style={{
              top: groupPermissionButtonRef.current
                ? groupPermissionButtonRef.current.getBoundingClientRect().bottom + 4
                : 0,
              left: groupPermissionButtonRef.current
                ? groupPermissionButtonRef.current.getBoundingClientRect().left
                : 0,
            }}
          >
            {(["view", "edit", "full"] as GroupAccessPermission[]).map((permission) => (
              <button
                key={permission}
                title={getGroupPermissionDescription(permission)}
                onClick={() => handleGroupPermissionChange(permission)}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1 rounded-md transition-colors min-h-[45px]",
                  groupPermission === permission
                    ? "bg-zinc-100 dark:bg-[rgb(42,42,42)]"
                    : "hover:bg-zinc-50 dark:hover:bg-[rgb(42,42,42)]"
                )}
              >
                <div className="flex items-center justify-center min-w-[20px] min-h-[20px]">
                  {getGroupPermissionIcon(permission)}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm text-zinc-900 dark:text-zinc-100">
                    {getGroupPermissionDisplay(permission)}
                  </div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">
                    {getGroupPermissionDescription(permission)}
                  </div>
                </div>
                {groupPermission === permission && (
                  <Check className="w-4 h-4 text-zinc-900 dark:text-zinc-100 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    </div>,
    document.body
  );
}

